/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import type { ProductPriceInfo as ProductCardPriceInfo } from "@farmlands/commerce";

import { formatPrice } from "../../../util/helpers";
import { CartItem } from "../../cart/storage/cart.types";
import { Label, PriceStyle } from "../components/ProductCard/types";
import { Product } from "../storage";

import { getProductPrices } from "./getPrices";
import { getProductStatus } from "./stock-status";

type PriceItem = Product | CartItem;

/** Maps product + cart pricing into @farmlands/commerce product card price props. */
export const getPriceInfo = (
  item: PriceItem,
  isTfo: boolean | undefined,
  quantity?: number,
  noPriceAvailable?: boolean
): ProductCardPriceInfo => {
  const resolvedNoPrice: boolean =
    noPriceAvailable ??
    getProductStatus(item.lifeCycleState, item.basePriceExcGst).noPriceAvailable;

  const items: Label[] = getProductPrices({
    item,
    quantity: quantity ?? ("quantity" in item ? item.quantity : undefined),
    isTfo,
  });

  const discounted: Label | undefined = items.find((entry) => entry.type === PriceStyle.Discounted);
  const [rrp, secondaryRrp]: Label[] = items.filter((entry) => entry.type === PriceStyle.RRP);
  const isEdited: boolean = discounted?.label?.toLowerCase().includes("edited") ?? false;

  return {
    label: isEdited ? "EDITED PRICE" : "YOUR PRICE",
    yourPrice: discounted?.value ?? "",
    yourPriceIncGst: discounted?.valueWithGst ? formatPrice(discounted.valueWithGst) : undefined,
    rrp: rrp?.value,
    secondaryRrp: secondaryRrp?.value,
    unavailable: resolvedNoPrice,
  };
};
